import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../auth/LoginModal';
import RazorpayPayment from '../payment/RazorpayPayment';
import ResponsiveReader from './ResponsiveReader';
import firebaseService from '../../services/firebaseService';

const OnlineBookAccess = ({ onClose, onBackToWebsite }) => {
  const { user, hasPurchased, login, setPurchaseStatus } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBookReader, setShowBookReader] = useState(false);
  const [readerType, setReaderType] = useState('standard'); // 'standard' or 'pageflip'

  const handleLogin = (userData, token) => {
    login(userData, token);
    setShowLoginModal(false);
  };

  const handlePaymentSuccess = async (response) => {
    if (user) {
      try {
        const orderData = {
          userId: user.uid,
          userName: user.name || user.phone || 'Reader',
          userEmail: user.email,
          product: 'web_access',
          amount: 99,
          currency: 'INR',
          razorpayPaymentId: response?.razorpay_payment_id || null,
          orderType: 'web_access',
          status: 'completed',
          paymentAmount: 99
        };
        const orderResult = await firebaseService.saveOrder(orderData);
        if (!orderResult.success) {
          console.error('Failed to save order in OnlineBookAccess:', orderResult.error, response?.razorpay_payment_id);
        }
      } catch (err) {
        console.error('Error saving order in OnlineBookAccess:', err);
      }
    }
    const accessResult = await setPurchaseStatus(true, 'web_access', 99);
    if (!accessResult.success) {
      console.error('Failed to persist book access in OnlineBookAccess:', accessResult.error, response?.razorpay_payment_id);
    }
    setShowPaymentModal(false);
    setShowBookReader(true);
  };

  const handleBackToWebsite = () => {
    setShowBookReader(false);
    onBackToWebsite();
  };

  if (showBookReader) {
    return (
      <ResponsiveReader
        onClose={() => setShowBookReader(false)}
        onBackToWebsite={handleBackToWebsite}
        usePageFlip={readerType === 'pageflip'}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-8 rounded-t-2xl text-center">
          <h2 className="text-3xl font-bold mb-4">Read Fijoli Online</h2>
          <p className="text-xl opacity-90">
            Experience the magic of Fijoli with our interactive online reader
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Features */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">✨ Interactive Features</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Realistic page-turning effects</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Two-page spread view</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Keyboard navigation</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Auto-updates from Google Docs</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">🔒 Secure Reading</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>No content copying</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>No screenshots allowed</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Protected from sharing</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Anti-piracy watermark</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pricing - TEMPORARILY COMMENTED OUT FOR TESTING */}
          {/* 
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">One-Time Purchase</h3>
              <div className="text-4xl font-bold text-red-600 mb-2 flex items-center justify-center gap-2">
                <span className="line-through text-gray-400 text-2xl font-normal">₹299</span>
                <span>₹99</span>
                <span className="text-xs text-orange-500 font-semibold px-2 py-0.5 bg-orange-100 rounded-full border border-orange-200 font-sans">
                  (early bird offer)
                </span>
              </div>
              <p className="text-gray-600 mb-4">Lifetime access to Fijoli online</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Unlimited reading sessions</li>
                <li>• Access from any device</li>
                <li>• Regular content updates</li>
                <li>• No subscription fees</li>
              </ul>
            </div>
          </div>
          */}


          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!user ? (
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Login to Continue
              </button>
            ) : !hasPurchased ? (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                Purchase Access - <span className="line-through text-white text-opacity-50 text-base font-normal">₹299</span> ₹99 <span className="text-xs text-orange-200 font-semibold bg-white bg-opacity-10 px-2 py-0.5 rounded-full border border-orange-300 border-opacity-30">(early bird offer)</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setReaderType('pageflip');
                  setShowBookReader(true);
                }}
                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Start Reading Now
              </button>
            )}

            {/* TEMPORARY TESTING BUTTON - NO RESTRICTIONS */}
            {/* 
            <button
              onClick={() => setShowBookReader(true)}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Start Reading Now (Testing Mode)
            </button>
            */}

            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>

          {/* Testing Notice */}
          {/* 
          <div className="mt-6 text-center">
            <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
              🧪 <strong>Testing Mode:</strong> Login and payment restrictions are temporarily disabled for testing purposes
            </p>
          </div>
          */}

          {/* User Status */}
          {user && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Logged in as: <span className="font-semibold">{user.name || user.email}</span>
                {hasPurchased && (
                  <span className="ml-2 text-green-600">✓ Access Granted</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showLoginModal && (
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onLogin={handleLogin}
          />
        )}

        {showPaymentModal && (
          <RazorpayPayment
            amount={99}
            onSuccess={handlePaymentSuccess}
            onFailure={() => setShowPaymentModal(false)}
            onClose={() => setShowPaymentModal(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default OnlineBookAccess;
